import { callProcedure, transformToProcedurePayload } from 'clickNgo/helpers/api'
import type { IProcedureResponse } from 'clickNgo/models/api'

import { mapProcedureRowToPartnerDevicesInfoResource } from '../lib/adapters'
import type { PartnerDevicesInfoProcedureRow } from '../model/types'

type PartnerDevicesInfoLegacyProcedureResponse = {
  Rows?: Array<{
    Data?: string | PartnerDevicesInfoProcedureRow[] | null
  }>
}

const parseLegacyProcedureRows = (
  responseData: PartnerDevicesInfoLegacyProcedureResponse
): PartnerDevicesInfoProcedureRow[] => {
  const rawData = responseData.Rows?.[0]?.Data

  if (Array.isArray(rawData)) {
    return rawData
  }

  if (typeof rawData !== 'string') {
    return []
  }

  try {
    const parsedData = JSON.parse(rawData) as unknown

    return Array.isArray(parsedData) ? (parsedData as PartnerDevicesInfoProcedureRow[]) : []
  } catch {
    throw new Error('Failed to parse partner devices info procedure response.')
  }
}

const getProcedureRows = (
  responseData:
    | IProcedureResponse<PartnerDevicesInfoProcedureRow[]>
    | PartnerDevicesInfoLegacyProcedureResponse
    | PartnerDevicesInfoProcedureRow[]
) => {
  if (Array.isArray(responseData)) {
    return responseData
  }

  if ('Data' in responseData && Array.isArray(responseData.Data)) {
    return responseData.Data
  }

  if ('Rows' in responseData) {
    return parseLegacyProcedureRows(responseData)
  }

  return []
}

export const fetchPartnerDevicesInfo = async ({
  partnerId,
  fYear,
}: {
  partnerId: string
  fYear: number
}) => {
  const response = await callProcedure<
    | IProcedureResponse<PartnerDevicesInfoProcedureRow[]>
    | PartnerDevicesInfoLegacyProcedureResponse
    | PartnerDevicesInfoProcedureRow[]
  >({
    url: '/webservice/621/sp/v3/spJBPDeviceInfo/call',
    method: 'POST',
    data: transformToProcedurePayload({
      partnerId,
      fYear,
    }),
  })

  if ('error' in response) {
    throw response.error
  }

  const rows = getProcedureRows(response.data)
  const row = rows[0]

  if (!row) {
    throw new Error('No partner devices info data available.')
  }

  return mapProcedureRowToPartnerDevicesInfoResource(row, {
    partnerId,
    fYear,
  })
}
